import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  EventSeat,
  Check,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  Wifi,
  AcUnit,
  LocalCafe,
  WaterDrop,
  Power,
  LocalParking,
  Security,
  MenuBook,
} from '@mui/icons-material';

// Amenity icons mapping
const amenityIcons = {
  wifi: <Wifi />,
  ac: <AcUnit />,
  'air conditioning': <AcUnit />,
  cafeteria: <LocalCafe />,
  cafe: <LocalCafe />,
  water: <WaterDrop />,
  'drinking water': <WaterDrop />,
  power: <Power />,
  'power backup': <Power />,
  parking: <LocalParking />,
  security: <Security />,
  'reading room': <MenuBook />,
  locker: <Security />,
};

const getAmenityIcon = (amenity) => {
  const key = amenity.toLowerCase();
  return amenityIcons[key] || <Check />;
};

// Placeholder images
const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

function ModernTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;

  const defaultDescription = `Welcome to ${library.name}! We provide a peaceful and productive environment for students and professionals. Visit us to experience our top-notch facilities and quiet study spaces.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '50vh', md: '70vh' },
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7))',
          }}
        />

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'white' },
              }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={nextImage}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'white' },
              }}
            >
              <ArrowForward />
            </IconButton>
          </>
        )}

        {/* Hero Content */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: { xs: 3, md: 6 },
            color: 'white',
          }}
        >
          <Container maxWidth="lg">
            <Typography variant="h2" fontWeight={700} sx={{ mb: 1, fontSize: { xs: '2rem', md: '3.5rem' } }}>
              {library.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationOn />
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                {library.address}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={onBookNow}
              sx={{
                bgcolor: accentColor,
                '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' },
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
              }}
            >
              Book Now
            </Button>
          </Container>
        </Box>

        {/* Image dots */}
        {images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              display: 'flex',
              gap: 1,
            }}
          >
            {images.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: idx === currentImageIndex ? 'white' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* About Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              About Us
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              {library.description || defaultDescription}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ bgcolor: 'grey.100', p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <EventSeat sx={{ color: accentColor, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight={700}>{library.capacity}</Typography>
                  <Typography color="text.secondary">Seats Available</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccessTime sx={{ color: accentColor, fontSize: 40 }} />
                <Box>
                  <Typography variant="h6" fontWeight={600}>Open Daily</Typography>
                  <Typography color="text.secondary">
                    {library.operating_hours || 'Contact for hours'}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Plans Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center" sx={{ mb: 4 }}>
            Membership Plans
          </Typography>

          {plans.length > 0 ? (
            <Grid container spacing={3} justifyContent="center">
              {plans.map((plan) => (
                <Grid item xs={12} sm={6} md={3} key={plan.id}>
                  <Card
                    elevation={0}
                    sx={{
                      border: '2px solid',
                      borderColor: 'grey.200',
                      borderRadius: 3,
                      height: '100%',
                      transition: 'all 0.3s',
                      '&:hover': {
                        borderColor: accentColor,
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Chip
                        label={plan.plan_type.replace('_', ' ').toUpperCase()}
                        size="small"
                        sx={{ mb: 2, bgcolor: `${accentColor}20`, color: accentColor }}
                      />
                      <Typography variant="h3" fontWeight={700} sx={{ color: accentColor }}>
                        ₹{plan.price_cents}
                      </Typography>
                      <Typography color="text.secondary" sx={{ mb: 3 }}>
                        {plan.plan_type === 'daily' && 'Per Day'}
                        {plan.plan_type === 'weekly' && 'Per Week'}
                        {plan.plan_type === 'monthly' && 'Per Month'}
                        {plan.plan_type === 'half_yearly' && 'Per 6 Months'}
                      </Typography>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={onBookNow}
                        sx={{
                          borderColor: accentColor,
                          color: accentColor,
                          '&:hover': { bgcolor: accentColor, color: 'white' },
                        }}
                      >
                        Select Plan
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Card elevation={0} sx={{ bgcolor: 'grey.100', p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>Contact us for membership details</Typography>
              <Button
                variant="contained"
                onClick={onBookNow}
                sx={{ bgcolor: accentColor, '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' } }}
              >
                Get in Touch
              </Button>
            </Card>
          )}
        </Container>
      </Box>

      {/* Gallery Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center" sx={{ mb: 4 }}>
          Gallery
        </Typography>
        <Grid container spacing={2}>
          {images.slice(0, 6).map((img, idx) => (
            <Grid item xs={6} md={4} key={idx}>
              <Box
                component="img"
                src={img}
                alt={`Gallery ${idx + 1}`}
                onClick={() => { setCurrentImageIndex(idx); setLightboxOpen(true); }}
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  height: 200,
                  objectFit: 'cover',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'scale(1.02)' },
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Amenities Section */}
      {library.amenities?.length > 0 && (
        <Box sx={{ bgcolor: 'white', py: 8 }}>
          <Container maxWidth="lg">
            <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center" sx={{ mb: 4 }}>
              Amenities
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              {library.amenities.map((amenity, idx) => (
                <Grid item xs={6} sm={4} md={2} key={idx}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Box sx={{ color: accentColor, mb: 1 }}>
                      {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 40 } })}
                    </Box>
                    <Typography variant="body2" fontWeight={500}>
                      {amenity}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Location Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center" sx={{ mb: 4 }}>
          Location
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: 300,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'grey.200',
              }}
            >
              {library.lat && library.lng ? (
                <iframe
                  title="Library Location"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.secondary">Map location unavailable</Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <LocationOn sx={{ color: accentColor, mt: 0.5 }} />
                <Box>
                  <Typography variant="h6" fontWeight={600}>Address</Typography>
                  <Typography color="text.secondary">{library.address}</Typography>
                </Box>
              </Box>
              {library.contact_phone && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Phone sx={{ color: accentColor, mt: 0.5 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Phone</Typography>
                    <Typography color="text.secondary">
                      <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {library.contact_phone}
                      </a>
                    </Typography>
                  </Box>
                </Box>
              )}
              {library.contact_email && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Email sx={{ color: accentColor, mt: 0.5 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Email</Typography>
                    <Typography color="text.secondary">
                      <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {library.contact_email}
                      </a>
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: accentColor, py: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={600} color="white" gutterBottom>
            Ready to Start Your Journey?
          </Typography>
          <Typography color="rgba(255,255,255,0.9)" sx={{ mb: 4 }}>
            Join {library.name} today and get access to a peaceful study environment.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onBookNow}
            sx={{
              bgcolor: 'white',
              color: accentColor,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              px: 4,
              py: 1.5,
            }}
          >
            Book Now
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#1a1a1a', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography color="rgba(255,255,255,0.7)">
              {library.name}
            </Typography>
            <Typography color="rgba(255,255,255,0.5)" variant="body2">
              Powered by{' '}
              <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: accentColor }}>
                BookMyLibrary
              </a>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'black' } }}
      >
        <IconButton
          onClick={() => setLightboxOpen(false)}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'white', zIndex: 1 }}
        >
          <Close />
        </IconButton>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt="Gallery"
          loading="lazy"
          decoding="async"
          sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />
      </Dialog>
    </Box>
  );
}

export default ModernTheme;
